import { notEmpty } from "@product/scmp-sdk";
import { type FunctionComponent, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ContentSchemaRender } from "scmp-app/components/schema-render/content";
import type { contentSubHeadlineContent$key } from "scmp-app/queries/__generated__/contentSubHeadlineContent.graphql";

import type { ContainerProps } from "./styles";
import { H2Container, H3Container } from "./styles";

export type Props = {
  className?: string;
  componentTag?: "h2" | "h3";
  disableFallbackEmptyToSummary?: boolean;
  reference: contentSubHeadlineContent$key;
  variant?: ContainerProps["$variant"];
};

export const ContentSubHeadline: FunctionComponent<Props> = ({
  className,
  componentTag,
  disableFallbackEmptyToSummary,
  reference,
  variant,
}) => {
  const content = useFragment(
    graphql`
      fragment contentSubHeadlineContent on Content {
        subHeadline {
          json
        }
        summary {
          json
        }
        ...contentSchemaRenderContent
      }
    `,
    reference,
  );

  const schema = useMemo(() => {
    const check = notEmpty(content.subHeadline?.json);
    if (disableFallbackEmptyToSummary && !check) return;
    return check ? content.subHeadline?.json : content.summary?.json;
  }, [content.subHeadline?.json, content.summary?.json, disableFallbackEmptyToSummary]);

  if (!notEmpty(schema)) return null;

  const ContainerWithTag = componentTag === "h2" ? H2Container : H3Container;
  return (
    <ContainerWithTag $variant={variant} className={className}>
      <ContentSchemaRender reference={content} schema={schema} />
    </ContainerWithTag>
  );
};

ContentSubHeadline.displayName = "ContentSubHeadline";
